<!-- src/components/admin/UnpaidBookings.vue -->
<template>
  <div class="unpaid-bookings">

    <!-- ===== Card Container ===== -->
    <v-card class="elevated-card mb-8">

      <!-- ===== Card Title with Year Select & Refresh ===== -->
      <v-card-title class="d-flex align-center justify-space-between">
        <h3 class="mb-0">Unpaid Bookings</h3>
        <div class="d-flex align-center">
          <v-select
            v-model="selectedYear"
            :items="availableYears"
            label="Select Year"
            class="mr-4"
            @change="fetchUnpaidBookings"
          />
          <v-btn color="primary" @click="fetchUnpaidBookings">
            Refresh Data
          </v-btn>
        </div>
      </v-card-title>

      <!-- ===== Card Text: Table ===== -->
      <v-card-text>
        <!-- Responsive container to handle horizontal scroll on smaller screens -->
        <div class="table-responsive">
          <table class="my-styled-table">
            <thead>
              <tr>
                <th @click="sortTable('id')" class="sortable">
                  ID
                  <v-icon small>{{ getSortIcon('id') }}</v-icon>
                </th>
                <th @click="sortTable('name')" class="sortable">
                  Name
                  <v-icon small>{{ getSortIcon('name') }}</v-icon>
                </th>
                <th @click="sortTable('phone')" class="sortable">
                  Phone
                  <v-icon small>{{ getSortIcon('phone') }}</v-icon>
                </th>
                <th @click="sortTable('start_date')" class="sortable">
                  Start Date
                  <v-icon small>{{ getSortIcon('start_date') }}</v-icon>
                </th>
                <th @click="sortTable('end_date')" class="sortable">
                  End Date
                  <v-icon small>{{ getSortIcon('end_date') }}</v-icon>
                </th>
                <th @click="sortTable('total_price')" class="sortable">
                  Total Price
                  <v-icon small>{{ getSortIcon('total_price') }}</v-icon>
                </th>
                <th @click="sortTable('total_paid')" class="sortable">
                  Total Paid
                  <v-icon small>{{ getSortIcon('total_paid') }}</v-icon>
                </th>
                <th @click="sortTable('numofdays')" class="sortable">
                  Days
                  <v-icon small>{{ getSortIcon('numofdays') }}</v-icon>
                </th>
                <th @click="sortTable('payment_status')" class="sortable">
                  Status
                  <v-icon small>{{ getSortIcon('payment_status') }}</v-icon>
                </th>
                <th @click="sortTable('note')" class="sortable">
                  Note
                  <v-icon small>{{ getSortIcon('note') }}</v-icon>
                </th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="item in paginatedBookings" :key="item.id">
                <td>{{ item.id }}</td>
                <td>{{ item.name }}</td>
                <td>{{ item.phone }}</td>
                <td>{{ item.start_date }}</td>
                <td>{{ item.end_date }}</td>
                <td>{{ item.total_price }}</td>
                <td>{{ item.total_paid }}</td>
                <td>{{ item.numofdays }}</td>
                <td>{{ item.payment_status }}</td>
                <td>{{ item.note || 'No Note' }}</td>
                <td>
                  <v-btn color="success" class="mr-2" @click="openPaymentStatusDialog(item)">
                    Change Payment Status
                  </v-btn>
                  <v-btn color="red" dark @click="confirmDelete(item)">
                    Delete
                  </v-btn>
                  <v-btn color="blue" dark class="ml-2" @click="openEditNoteDialog(item)">
                    Edit Note
                  </v-btn>
                </td>
              </tr>

              <!-- If no data -->
              <tr v-if="paginatedBookings.length === 0">
                <td :colspan="11" class="text-center">
                  No unpaid bookings found.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </v-card-text>

      <!-- ===== Pagination ===== -->
      <v-card-actions v-if="totalPages > 1" class="justify-center">
        <v-pagination
          v-model="currentPage"
          :length="totalPages"
          @input="onPageChange"
        />
      </v-card-actions>
    </v-card>

    <!-- =================== Confirm Delete Dialog =================== -->
    <v-dialog v-model="showDeleteDialog" max-width="450">
      <v-card>
        <v-card-title class="text-h5">Confirm Deletion</v-card-title>
        <v-card-text>
          Are you sure you want to delete booking ID {{ bookingToDelete?.id }}?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="grey" @click="showDeleteDialog = false">
            Cancel
          </v-btn>
          <v-btn text color="red" @click="deleteBookingNow">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- =================== Edit Note Dialog =================== -->
    <v-dialog v-model="showEditNoteDialog" max-width="600">
      <v-card>
        <v-card-title>
          <span class="text-h5">Edit Note for Booking ID {{ editNoteData.id }}</span>
        </v-card-title>
        <v-card-text>
          <v-form ref="editNoteForm">
            <v-textarea
              label="Note"
              v-model="editNoteData.note"
              required
            ></v-textarea>
          </v-form>
          <v-alert type="error" v-if="editNoteErrorMsg" class="mt-2">
            {{ editNoteErrorMsg }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="showEditNoteDialog = false">
            Cancel
          </v-btn>
          <v-btn color="blue" text @click="saveNote">
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- =================== Change Payment Status Dialog =================== -->
    <v-dialog v-model="showPaymentDialog" max-width="450">
      <v-card>
        <v-card-title class="text-h5">
          Change Payment Status for Booking ID {{ paymentDialogData.id }}
        </v-card-title>
        <v-card-text>
          <v-form ref="changePaymentForm">
            <v-select
              v-model="paymentDialogData.status"
              :items="['paid', 'partial', 'unpaid']"
              label="Select Payment Status"
              required
            ></v-select>
            <v-text-field
              v-if="paymentDialogData.status === 'partial'"
              v-model="paymentDialogData.partialAmount"
              label="Enter partial payment amount"
              type="number"
              min="0"
            ></v-text-field>
          </v-form>
          <v-alert type="error" v-if="paymentDialogErrorMsg" class="mt-2">
            {{ paymentDialogErrorMsg }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="showPaymentDialog = false">
            Cancel
          </v-btn>
          <v-btn color="blue" text @click="updatePaymentStatus">
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import apiService from "@/services/apiService.js";

export default {
  name: "UnpaidBookings",
  props: {
    token: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      unpaidBookings: [],
      availableYears: [],
      selectedYear: null,
      errorMsg: null,

      // Sorting & Pagination
      sortKey: "id",
      sortDesc: false,
      currentPage: 1,
      itemsPerPage: 20,

      // Dialog states
      showDeleteDialog: false,
      bookingToDelete: null,

      showEditNoteDialog: false,
      editNoteData: {
        id: null,
        note: "",
      },
      editNoteErrorMsg: null,

      showPaymentDialog: false,
      paymentDialogData: {
        id: null,
        status: "unpaid",
        partialAmount: 0,
      },
      paymentDialogErrorMsg: null,
    };
  },
  computed: {
    // Sort the data first, then slice for pagination
    sortedBookings() {
      return this.sortData(this.unpaidBookings, this.sortKey, this.sortDesc);
    },
    paginatedBookings() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.sortedBookings.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.unpaidBookings.length / this.itemsPerPage);
    },
  },
  methods: {
    fetchUnpaidBookings() {
      this.errorMsg = null;
      apiService
        .fetchUnpaidBookings(this.token)
        .then((res) => {
          const allData = res.data || [];
          this.buildAvailableYears(allData);

          // Filter by year
          const filtered = this.selectedYear
            ? allData.filter((b) => {
                if (!b.start_date) return false;
                const year = new Date(b.start_date + "T00:00:00").getFullYear();
                return year === this.selectedYear;
              })
            : allData;

          this.unpaidBookings = filtered;
          // Reset to first page after new fetch
          this.currentPage = 1;
        })
        .catch((err) => {
          this.$emit(
            "error",
            err.response?.data?.error || "Failed to fetch unpaid bookings."
          );
        });
    },

    buildAvailableYears(allData) {
      const yearSet = new Set();
      for (const b of allData) {
        if (!b.start_date) continue;
        const d = new Date(b.start_date + "T00:00:00");
        if (!isNaN(d.getTime())) {
          yearSet.add(d.getFullYear());
        }
      }
      const arr = Array.from(yearSet).sort((a, b) => a - b);
      this.availableYears = arr;

      // If no year is selected yet, default to the latest
      if (!this.selectedYear && arr.length > 0) {
        this.selectedYear = arr[arr.length - 1];
      }
    },

    // Sorting
    sortTable(key) {
      if (this.sortKey === key) {
        this.sortDesc = !this.sortDesc;
      } else {
        this.sortKey = key;
        this.sortDesc = false;
      }
    },
    getSortIcon(key) {
      if (this.sortKey === key) {
        return this.sortDesc ? "mdi-arrow-down" : "mdi-arrow-up";
      }
      return "mdi-sort";
    },
    sortData(array, key, desc) {
      return array.slice().sort((a, b) => {
        let result = 0;

        // Numeric fields
        if (["total_price", "total_paid", "numofdays"].includes(key)) {
          result = Number(a[key]) - Number(b[key]);
        }
        // Date fields
        else if (key === "start_date" || key === "end_date") {
          result = new Date(a[key]) - new Date(b[key]);
        }
        // Generic string or other fields
        else {
          if (a[key] < b[key]) result = -1;
          if (a[key] > b[key]) result = 1;
        }

        return desc ? -result : result;
      });
    },

    onPageChange() {
      // Hook for page change if needed
    },

    // Change Payment Status
    openPaymentStatusDialog(item) {
      this.paymentDialogData = {
        id: item.id,
        status: item.payment_status || "unpaid",
        partialAmount: 0,
      };
      this.paymentDialogErrorMsg = null;
      this.showPaymentDialog = true;
    },
    updatePaymentStatus() {
      if (
        this.paymentDialogData.status === "partial" &&
        !this.paymentDialogData.partialAmount
      ) {
        this.paymentDialogErrorMsg = "Please enter the partial payment amount.";
        return;
      }
      apiService
        .confirmPayment(
          this.paymentDialogData.id,
          {
            status: this.paymentDialogData.status,
            partial_amount: this.paymentDialogData.partialAmount,
          },
          this.token
        )
        .then(() => {
          this.showPaymentDialog = false;
          this.fetchUnpaidBookings();
        })
        .catch((err) => {
          this.paymentDialogErrorMsg =
            err.response?.data?.error || "Failed to update payment status.";
        });
    },

    // Delete Booking
    confirmDelete(item) {
      this.bookingToDelete = item;
      this.showDeleteDialog = true;
    },
    deleteBookingNow() {
      if (!this.bookingToDelete) {
        this.showDeleteDialog = false;
        return;
      }
      apiService
        .deleteBooking(this.bookingToDelete.id, this.token)
        .then(() => {
          this.showDeleteDialog = false;
          this.bookingToDelete = null;
          this.fetchUnpaidBookings();
        })
        .catch((err) => {
          this.$emit(
            "error",
            err.response?.data?.error || "Failed to delete booking."
          );
          this.showDeleteDialog = false;
          this.bookingToDelete = null;
        });
    },

    // Edit Note
    openEditNoteDialog(item) {
      this.editNoteData = {
        id: item.id,
        note: item.note || "",
      };
      this.editNoteErrorMsg = null;
      this.showEditNoteDialog = true;
    },
    saveNote() {
      if (!this.editNoteData.note) {
        this.editNoteErrorMsg = "Note cannot be empty.";
        return;
      }
      apiService
        .addEditNote(this.editNoteData.id, this.editNoteData.note, this.token)
        .then(() => {
          this.showEditNoteDialog = false;
          this.editNoteData = {
            id: null,
            note: "",
          };
          this.fetchUnpaidBookings();
        })
        .catch((err) => {
          this.editNoteErrorMsg =
            err.response?.data?.error || "Failed to save note.";
        });
    },
  },
  watch: {
    // Re-fetch if year changes
    selectedYear() {
      this.fetchUnpaidBookings();
    },
  },
  mounted() {
    this.fetchUnpaidBookings();
  },
};
</script>

<style scoped>
/* ===== Card styling ===== */
.elevated-card {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

/* ===== Table styling ===== */
.table-responsive {
  overflow-x: auto; /* horizontal scroll on small screens */
}

.my-styled-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}

.my-styled-table thead tr {
  background-color: #f5f5f5;
  color: #333;
}

.my-styled-table th,
.my-styled-table td {
  padding: 14px 20px;
  text-align: left;
  vertical-align: middle;
  white-space: nowrap;
  border-bottom: 1px solid #eee;
}

/* Zebra-striping for the table body */
.my-styled-table tbody tr:nth-child(even) {
  background-color: #fafafa;
}

.sortable {
  cursor: pointer;
  user-select: none;
  position: relative;
  white-space: nowrap;
}

.sortable .v-icon {
  vertical-align: middle;
  margin-left: 4px;
}

/* ===== Utility Classes ===== */
.text-center {
  text-align: center;
}
.ml-2 {
  margin-left: 8px;
}
.mr-2 {
  margin-right: 8px;
}
.mr-4 {
  margin-right: 16px;
}
.mb-8 {
  margin-bottom: 32px;
}
.mt-2 {
  margin-top: 8px;
}
</style>
